import { useEffect, useState } from "react";

import { buildSilentAuthUrl, isCognitoConfigured } from "../services/cognito.service";
import { hadCognitoTokenOnLoad } from "./useCognitoTokenExchange";

// Most are publicly accessible pages (login, sign-up, etc).
// /callback is included as a fallback, but it also gets an explicit
// hard stop below because an auth round-trip is actively in progress there.
const PUBLIC_ROUTES = [
	"/callback",
	"/login",
	"/sign-up",
	"/forgot-password",
	"/reset-password",
	"/error",
	"/faq",
	"/contact-us",
];

// These routes have dynamic segments (e.g. /guest-dashboard-access/abc123)
// so we check the path prefix instead of an exact match.
// /tenant-invite/ is included because the TenantHandshake component handles
// its own auth logic internally -- it will redirect to Cognito itself if the
// invited user is an SSO-only user, so the silent check must not run first.
const PUBLIC_ROUTE_PREFIXES = ["/guest-dashboard-access/", "/guest-dashboard-entry/", "/tenant-invite/"];

// Returns true if the current path is in the public routes list or matches
// a public prefix. The /callback hard stop is handled separately below.
const isPublicRoute = () => {
	const path = window.location.pathname;

	if (PUBLIC_ROUTES.includes(path)) {
		return true;
	}

	return PUBLIC_ROUTE_PREFIXES.some((prefix) => path.startsWith(prefix));
};

// This hook silently checks if the user already has an active Cognito
// session when they land on the app with no local Bluechip session.
//
// Flow:
// 1. User lands on a protected route with no local Bluechip auth state.
// 2. We redirect them to Cognito with prompt=none.
// 3. If Cognito already has a valid session, it redirects back to /callback
//    with an authorization code.
// 4. If Cognito does not have a valid session, it redirects back to /callback
//    with an error and the app can fall back to the login page.
const useSilentCognitoAuth = (hasToken, hasUser) => {
	const [isCheckingCognito, setIsCheckingCognito] = useState(false);

	useEffect(() => {
		const isAlreadyLoggedIn = hasToken && hasUser;

		// Skip the silent check if the user is already logged in or Cognito is not set up.
		if (isAlreadyLoggedIn || !isCognitoConfigured()) {
			return;
		}

		// Skip the silent check if the user arrived with a cognito_token in the URL.
		// We use the module-level constant here because by the time this effect runs,
		// useCognitoTokenExchange has already removed the token from the URL -- so
		// checking window.location.search here would always return false.
		if (hadCognitoTokenOnLoad) {
			return;
		}

		// Hard stop -- never redirect away from /callback under any circumstances.
		// CognitoCallback is actively processing an auth code here. Redirecting
		// would cancel that in-flight exchange and start an infinite loop.
		if (window.location.pathname === "/callback") {
			return;
		}

		const routerSavedIntendedUrl = localStorage.getItem("cognito_intended_url");

		// On all other public routes, only skip if there is no pending destination.
		// If the router saved one, we still need to proceed with the silent check.
		if (isPublicRoute() && !routerSavedIntendedUrl) {
			return;
		}

		// Only save the current path if we are on a protected route.
		// If we are already on a public route, the router already saved the
		// correct destination -- do not overwrite it with /login or similar.
		if (!isPublicRoute()) {
			localStorage.setItem(
				"cognito_intended_url",
				JSON.stringify({
					url: window.location.pathname + window.location.search,
					savedAt: Date.now(),
				}),
			);
		}

		const silentAuthUrl = buildSilentAuthUrl();
		if (silentAuthUrl) {
			setIsCheckingCognito(true);
			window.location.href = silentAuthUrl;
		}
	}, [hasToken, hasUser]);

	return { isCheckingCognito };
};

export default useSilentCognitoAuth;
